import type { SeriesInstanceUid } from '~/domain/dicom/uids';
import { windowLevelOfPreset, WindowLevel } from '~/domain/patient/windowLevel';
import type { BlendSettings } from '~/domain/viewport/fusionBlend';
import {
    BlendAdapter,
    BlendCandidate,
    BlendSource,
    EnvironmentAdapter,
    FrameRotationSource,
    WindowingAdapter,
    WindowLevelDelta,
    WindowLevelSource,
} from '../adapters/types';
import { ViewportId } from '../layout/types';
import { WindowLevelFanout } from '../linking/useWindowLevelFanout';
import { PairEnd } from '../pairEndLabels';
import { histogramTintOf, WindowHistogramImage } from './WindowHistogram';

/**
 * Maps the window/level fan-out policy and the windowing port, the blend
 * port, and the environment port's frame rotation, to the props the row
 * footer and the tiles render.
 */

/** One image's window/level in the footer: a read source and its writes. */
export interface RowWindowingControls {
    readonly source: WindowLevelSource;
    /** Sets the image's own window; no other series is touched. */
    readonly onSetWindowLevel: (windowLevel: WindowLevel) => void;
    /** Moves the image's own window by a step; no other series is touched. */
    readonly onNudgeWindowLevel: (delta: WindowLevelDelta) => void;
    /**
     * Applies the named preset to every series it reaches. Each takes the
     * window its own list gives under that name, so "Acquisition" and "Auto"
     * give each series its own numbers; a series that lists the name as
     * unavailable, or not at all, keeps its window.
     */
    readonly onSelectPreset: (presetName: string) => void;
}

/**
 * Sets the named preset on one series, from that series' own list. Does
 * nothing when the series lists the preset as unavailable or not at all.
 */
function applyPresetByName(
    windowing: WindowingAdapter,
    seriesInstanceUid: SeriesInstanceUid,
    presetName: string
): void {
    const preset = windowing
        .sourceFor(seriesInstanceUid)
        .getPresets()
        .find((candidate) => candidate.name === presetName);
    if (!preset || preset.disabled) {
        return;
    }
    windowing.setWindowLevel(seriesInstanceUid, windowLevelOfPreset(preset));
}

/**
 * Binds the footer to a row's own image. Reads come from the row's series.
 * A typed value and a step write that series alone; a named preset fans out
 * to every series `windowLevelTargets` names for the row's tile (an empty
 * target set writes nothing).
 */
export function resolveRowWindowing(
    windowLevelTargets: WindowLevelFanout,
    id: ViewportId,
    seriesInstanceUid: SeriesInstanceUid,
    windowing: WindowingAdapter
): RowWindowingControls {
    return {
        ...resolveSeriesWindowing(seriesInstanceUid, windowing),
        onSelectPreset: (presetName) =>
            windowLevelTargets(id).forEach((uid) =>
                applyPresetByName(windowing, uid, presetName)
            ),
    };
}

/**
 * Binds the footer to one series alone: every write reaches that series
 * only, and a named preset takes the window from that series' own list. For
 * an image a row fused rather than bound, the fan-out policy has no answer:
 * it answers for tiles, and a fused image occupies none. Its window belongs
 * to the row that fused it and must not reach a row that merely shares its
 * frame of reference.
 */
export function resolveSeriesWindowing(
    seriesInstanceUid: SeriesInstanceUid,
    windowing: WindowingAdapter
): RowWindowingControls {
    return {
        source: windowing.sourceFor(seriesInstanceUid),
        onSetWindowLevel: (windowLevel) =>
            windowing.setWindowLevel(seriesInstanceUid, windowLevel),
        onNudgeWindowLevel: (delta) =>
            windowing.nudgeWindowLevel(seriesInstanceUid, delta),
        onSelectPreset: (presetName) =>
            applyPresetByName(windowing, seriesInstanceUid, presetName),
    };
}

/**
 * The histograms of the images a tile draws, in the order it lists them
 * (`drawn`, each with its pair name while drawn with the other image). Each
 * reads its own series and is coloured by its end of the row's pair: the
 * row's own image by the primary end, the fused image by the secondary,
 * whichever of them the tile draws. An image that is neither the row's
 * nor its fused one has no histogram.
 */
/** One image of a pair, with the series it is. */
type KeyedPairEnd = PairEnd & { readonly seriesInstanceUid: SeriesInstanceUid };

export function resolvePaneHistograms(
    drawn: ReadonlyArray<{
        readonly seriesInstanceUid: SeriesInstanceUid;
        readonly pairLabel?: string;
    }>,
    rowImage: KeyedPairEnd,
    fused: KeyedPairEnd | undefined,
    blend: BlendSettings,
    windowing: WindowingAdapter
): ReadonlyArray<WindowHistogramImage> {
    const pairModalities = fused
        ? [rowImage.modality, fused.modality]
        : undefined;
    return drawn.flatMap(({ seriesInstanceUid, pairLabel }) => {
        const isRowImage = seriesInstanceUid === rowImage.seriesInstanceUid;
        const image = isRowImage
            ? rowImage
            : fused?.seriesInstanceUid === seriesInstanceUid
              ? fused
              : undefined;
        if (!image) {
            return [];
        }
        return [
            {
                source: windowing.sourceFor(seriesInstanceUid),
                seriesInstanceUid,
                modality: image.modality,
                tint: histogramTintOf(
                    isRowImage ? 'primary' : 'secondary',
                    blend,
                    pairModalities
                ),
                testIdSuffix: isRowImage ? 'row' : 'fused',
                pairLabel,
            },
        ];
    });
}

/** The row footer's blend state: a per-series source + fuse/clear/blend writes. */
export interface RowBlendControls {
    readonly source: BlendSource;
    readonly onFuse: (candidate: BlendCandidate) => void;
    readonly onClear: () => void;
    readonly onSetBlend: (patch: Partial<BlendSettings>) => void;
}

/**
 * Binds a row's footer to the blend state of the row's image. Unlike a
 * named window preset, nothing fans a fuse out to another series; every row
 * that shows the same series shares its one blend.
 */
export function resolveRowBlend(
    seriesInstanceUid: SeriesInstanceUid,
    blend: BlendAdapter
): RowBlendControls {
    return {
        source: blend.sourceFor(seriesInstanceUid),
        onFuse: (candidate) => blend.fuse(seriesInstanceUid, candidate),
        onClear: () => blend.clear(seriesInstanceUid),
        onSetBlend: (patch) => blend.setBlend(seriesInstanceUid, patch),
    };
}

/** A row's frame state: the row's frame source and its reset. */
export interface RowFrameControls {
    readonly source: FrameRotationSource;
    readonly onReset: () => void;
}

/**
 * Binds a row's tiles to the oblique frame of the row's image. The frame
 * belongs to the image, not to the row: every row that shows the same
 * series shares one Environment and so one frame, and a reset returns all
 * of those rows to their patient-aligned planes.
 */
export function resolveRowFrame(
    seriesInstanceUid: SeriesInstanceUid,
    environment: EnvironmentAdapter
): RowFrameControls {
    return {
        source: environment.frameRotationSourceFor(seriesInstanceUid),
        onReset: () => environment.resetFrameRotation(seriesInstanceUid),
    };
}
